import { Controller, useWatch, type Control, type UseFormGetValues, type UseFormRegister, type UseFormSetValue } from 'react-hook-form';
import { Calculator, Eraser } from 'lucide-react';
import { Button, DateField, FormField, NumberField, TextField, useToast } from '@/components';
import { useCodeValues } from '@/features/masters';
import { errorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { calcDevelopment, calcFuelTax, calcLubricant } from '../api';
import type { VoyageForm } from '../form';
import '../voyage-reports.css';
import { ControlledSelect } from '@/features/advance-notices/components/ControlledSelect';

/** 燃油代・潤滑油・現像代（計算は /api/calc/* で行い、結果を設定する） */
export function ExpensePanel({
  control,
  register,
  setValue,
  getValues,
  fiscalYear,
  businessOperatorId,
}: {
  control: Control<VoyageForm>;
  register: UseFormRegister<VoyageForm>;
  setValue: UseFormSetValue<VoyageForm>;
  getValues: UseFormGetValues<VoyageForm>;
  fiscalYear: number;
  businessOperatorId: number;
}) {
  const toast = useToast();
  const { data: codes } = useCodeValues();
  const e = useWatch({ control, name: 'expense' });
  const periodTo = useWatch({ control, name: 'periodTo' });
  const set = (k: keyof VoyageForm['expense'], v: number | string | null) => setValue(`expense.${k}`, v as never, { shouldDirty: true });

  const fuel = async (mode: 'consumption' | 'lightOil') => {
    const x = getValues('expense');
    try {
      const r = await calcFuelTax({
        mode,
        returnDate: periodTo || null,
        fuelDate: x.fuelDate || null,
        fuelType: x.fuelType || null,
        taxRounding: x.fuelTaxRounding,
        total: x.fuelTotal ?? 0,
        taxAmount: mode === 'consumption' ? x.fuelTax || null : null,
        quantity: x.fuelQuantity,
      });
      set('fuelAmountExclTax', r.amountExclTax);
      set('fuelTax', r.tax);
      set('fuelLightOilTax', r.lightOilTax || null);
      toast.success('燃油代を計算しました', `税抜 ${formatNumber(r.amountExclTax)}円・消費税 ${formatNumber(r.tax)}円（税率 ${Math.round(r.taxRate * 100)}%）`);
    } catch (err) {
      toast.error('計算できませんでした', errorMessage(err));
    }
  };
  const lube = async () => {
    const x = getValues('expense');
    try {
      const r = await calcLubricant({ fiscalYear, businessOperatorId, returnDate: periodTo || null, fuelQuantity: x.fuelQuantity, unitPrice: x.lubeUnitPrice });
      set('lubeCoefficient', r.coefficient);
      set('lubeAmountExclTax', r.amountExclTax);
      set('lubeTotal', r.total);
    } catch (err) {
      toast.error('計算できませんでした', errorMessage(err));
    }
  };
  const dev = async () => {
    const x = getValues('expense');
    try {
      const r = await calcDevelopment({ returnDate: periodTo || null, devDate: x.devDate || null, taxRounding: x.devTaxRounding, total: x.devTotal ?? 0, taxAmount: x.devTax || null });
      set('devAmountExclTax', r.amountExclTax);
      set('devTax', r.tax);
    } catch (err) {
      toast.error('計算できませんでした', errorMessage(err));
    }
  };
  const num = (k: keyof VoyageForm['expense'], label: string, unit = '円', decimals = 0, readOnly = false) => (
    <FormField label={label}>
      <Controller control={control} name={`expense.${k}` as 'expense.fuelTotal'} render={({ field }) => <NumberField {...field} unit={unit} decimals={decimals} width="full" readOnly={readOnly} aria-label={label} />} />
    </FormField>
  );
  const roundingOpts = (codes?.fuelTaxRoundings ?? ['四捨五入', '切り捨て']).map((v) => ({ value: v, label: v }));

  return (
    <div className="bs-expense">
      <section className="bs-expense__card">
        <h3 className="bs-expense__title">
          燃油代
          <span className="bs-expense__tools">
            <Button size="sm" variant="primary" icon={<Calculator size={14} />} onClick={() => void fuel('consumption')}>
              消費税
            </Button>
            <Button size="sm" icon={<Calculator size={14} />} onClick={() => void fuel('lightOil')} title="帰港日がインボイス制度開始前（2023/9/30 以前）の軽油(引取税計算有)のみ">
              引取税
            </Button>
            <Button
              size="sm"
              variant="ghost"
              icon={<Eraser size={14} />}
              onClick={() => (['fuelDate', 'fuelQuantity', 'fuelUnitPrice', 'fuelLightOilTax', 'fuelAmountExclTax', 'fuelTax', 'fuelTotal', 'fuelNote'] as const).forEach((k) => set(k, k === 'fuelDate' || k === 'fuelNote' ? '' : null))}
            >
              クリア
            </Button>
          </span>
        </h3>
        <div className="bs-expense__grid">
          <FormField label="給油日" hint="帰港日以降の日付">
            <DateField {...register('expense.fuelDate')} />
          </FormField>
          <FormField label="燃油種類">
            <ControlledSelect control={control} name="expense.fuelType" placeholder="—" options={(codes?.fuelTypes ?? []).map((v) => ({ value: v, label: v }))} />
          </FormField>
          <FormField label="税算出">
            <ControlledSelect control={control} name="expense.fuelTaxRounding" options={roundingOpts} />
          </FormField>
          {num('fuelQuantity', '数量', 'ℓ', 2)}
          {num('fuelUnitPrice', '単価', '円/ℓ', 2)}
          {num('fuelTotal', '合計金額（税込）')}
          {num('fuelTax', '消費税（任意入力）')}
          {num('fuelLightOilTax', '軽油引取税', '円', 0, true)}
          {num('fuelAmountExclTax', '金額（税抜）', '円', 0, true)}
          <FormField label="備考" className="is-full" hint="給油遅延・消費量超過の理由など">
            <TextField {...register('expense.fuelNote')} />
          </FormField>
        </div>
      </section>
      <section className="bs-expense__card">
        <h3 className="bs-expense__title">
          潤滑油
          <span className="bs-expense__tools">
            <Button size="sm" variant="primary" icon={<Calculator size={14} />} onClick={() => void lube()}>
              計算
            </Button>
            <Button size="sm" variant="ghost" icon={<Eraser size={14} />} onClick={() => (['lubeDate', 'lubeUnitPrice', 'lubeCoefficient', 'lubeAmountExclTax', 'lubeTotal'] as const).forEach((k) => set(k, k === 'lubeDate' ? '' : null))}>
              クリア
            </Button>
          </span>
        </h3>
        <div className="bs-expense__grid">
          <FormField label="給油日（請求領収日付）">
            <DateField {...register('expense.lubeDate')} />
          </FormField>
          {num('lubeUnitPrice', '単価', '円/ℓ', 2)}
          {num('lubeCoefficient', '潤滑油係数', '', 4, true)}
          {num('lubeAmountExclTax', '金額（税抜）', '円', 0, true)}
          {num('lubeTotal', '合計金額', '円', 0, true)}
        </div>
        <p className="bs-cell-sub">金額 ＝ 燃油数量 × 潤滑油係数 × 単価（切り捨て）。消費税は計上しません。</p>
      </section>
      <section className="bs-expense__card">
        <h3 className="bs-expense__title">
          現像代（消耗品費）
          <span className="bs-expense__tools">
            <Button size="sm" variant="primary" icon={<Calculator size={14} />} onClick={() => void dev()}>
              計算
            </Button>
            <Button size="sm" variant="ghost" icon={<Eraser size={14} />} onClick={() => (['devDate', 'devAmountExclTax', 'devTax', 'devTotal'] as const).forEach((k) => set(k, k === 'devDate' ? '' : null))}>
              クリア
            </Button>
          </span>
        </h3>
        <div className="bs-expense__grid">
          <FormField label="現像日">
            <DateField {...register('expense.devDate')} />
          </FormField>
          <FormField label="税算出">
            <ControlledSelect control={control} name="expense.devTaxRounding" options={roundingOpts} />
          </FormField>
          {num('devTotal', '合計金額（税込）')}
          {num('devTax', '消費税（任意入力）')}
          {num('devAmountExclTax', '金額（税抜）', '円', 0, true)}
        </div>
        <div className="bs-expense__total">
          <span>燃油＋潤滑油＋現像代（税込）</span>
          <span className="bs-calc-amount">{formatNumber((e?.fuelTotal ?? 0) + (e?.lubeTotal ?? 0) + (e?.devTotal ?? 0))}円</span>
        </div>
      </section>
    </div>
  );
}
